/**
 * Artwork AI metadata — request it after an upload lands, and deliver the result to an
 * edit screen that may or may not be open yet.
 *
 * This is the browser half of what `desktop/gemini.php` used to do on a cron. The request
 * is fired per upload and NOT awaited by the uploader: a Gemini round trip takes seconds,
 * and blocking the "processing…" card on it would stall a directory upload of 200 files.
 *
 * Results are cached per artwork id because the two events race. The artist can open the
 * edit modal before the answer arrives (subscriber first) or after it (result first), and
 * both have to work — a late subscriber is handed the cached result immediately.
 */
import { api } from "../api";

export interface AiMetadataResult {
  id: number;
  title: string | null;
  tags: string | null;
  artworkType: string | null;
  categoryId: number | null;
  unmatchedCategory: string | null;
  failed: string[];
}

type Listener = (result: AiMetadataResult) => void;

const results = new Map<number, AiMetadataResult>();
const listeners = new Map<number, Set<Listener>>();
/** Artworks with a request already in flight, so a re-render cannot double-charge the API. */
const inFlight = new Set<number>();

/**
 * Subscribe to the AI result for one artwork. Fires immediately if it has already arrived.
 * Returns an unsubscribe function for the effect cleanup.
 */
export function onAiMetadata(artworkId: number, fn: Listener): () => void {
  const existing = results.get(artworkId);
  if (existing) fn(existing);

  let set = listeners.get(artworkId);
  if (!set) {
    set = new Set();
    listeners.set(artworkId, set);
  }
  set.add(fn);

  return () => {
    const s = listeners.get(artworkId);
    if (!s) return;
    s.delete(fn);
    if (s.size === 0) listeners.delete(artworkId);
  };
}

/**
 * Ask the backend to generate metadata for a freshly uploaded artwork.
 *
 * Failures are swallowed on purpose. The artwork is already uploaded and editable by hand;
 * a missing API key or a Gemini outage must not put an error in front of an artist who did
 * nothing wrong. The server logs the detail.
 */
export function requestAiMetadata(artworkId: number): void {
  if (!Number.isFinite(artworkId) || artworkId <= 0) return;
  if (inFlight.has(artworkId) || results.has(artworkId)) return;
  inFlight.add(artworkId);

  void api.artwork
    .aiMetadata({ id: artworkId })
    .then((result) => {
      results.set(artworkId, result);
      for (const fn of listeners.get(artworkId) ?? []) fn(result);
    })
    .catch(() => {
      // Intentionally silent — see above.
    })
    .finally(() => {
      inFlight.delete(artworkId);
    });
}

/**
 * Whether an element is the one the artist is currently typing into.
 *
 * This is the guard behind "never overwrite their input while they are in the field":
 * the AI answer arrives seconds after the upload, by which time the artist may already be
 * mid-word in the title box. Focus is the signal — an unfocused field is safe to fill, a
 * focused one is theirs until they leave it.
 */
export function isActivelyEdited(el: Element | null | undefined): boolean {
  return el != null && document.activeElement === el;
}

/**
 * Fields the artist has typed into or changed themselves, keyed by artwork.
 *
 * `isActivelyEdited` only covers the field they are in *right now*. This covers the one
 * they have already moved on from: a title they typed and left, or a category they picked,
 * is theirs for good, and an answer arriving seconds later must not overwrite it.
 *
 * Module-level rather than card state because the uploader re-mounts a card on every save
 * (`directoryUpdate` → `directoryRefresh` → `mountDirectoryCard`). State held inside the
 * card would be wiped by the very action that proves the artist had edited it.
 */
const artistEdited = new Map<number, Set<string>>();

/** Record that the artist has typed in / changed `field` on this artwork. */
export function markArtistEdited(artworkId: number, field: string): void {
  let fields = artistEdited.get(artworkId);
  if (!fields) {
    fields = new Set();
    artistEdited.set(artworkId, fields);
  }
  fields.add(field);
}

/** Whether the artist has made `field` on this artwork their own. */
export function isArtistEdited(artworkId: number, field: string): boolean {
  return artistEdited.get(artworkId)?.has(field) === true;
}
